import * as React from 'react';
import {
  DesktopLargeBreakpointIcon,
  DesktopStarBreakpointIcon,
  DesktopXLBreakpointIcon,
  DesktopXXLBreakpointIcon,
  DeviceDesktop24Icon,
  DeviceDesktopXL24Icon,
  DeviceDesktopXXL24Icon,
  DeviceLaptopStar24Icon,
  DeviceMobileLandscape24Icon,
  DeviceMobilePortrait24Icon,
  DeviceTabletPortrait24Icon,
  MobileBreakpointIcon,
  MobileLandscapeBreakpointIcon,
  TabletPortraitBreakpointIcon,
  type TextColor,
  type SpringIconType,
} from '@packages/systems/spring';
import type {BreakpointsMap, BreakpointID} from '@packages/systems/style/types';
import {
  BREAKPOINT_ID_XXL,
  BREAKPOINT_ID_XL,
  BREAKPOINT_ID_LARGE,
  BREAKPOINT_ID_MAIN,
  BREAKPOINT_ID_MEDIUM,
  BREAKPOINT_ID_SMALL,
  BREAKPOINT_ID_TINY,
} from '@packages/systems/style/breakpoint-ids';
import {pickBreakpointIcon} from './pickIcon';

const DeviceDesktopXXLarge = pickBreakpointIcon(
  DesktopXXLBreakpointIcon,
  DeviceDesktopXXL24Icon
);
const DeviceDesktopXLarge = pickBreakpointIcon(
  DesktopXLBreakpointIcon,
  DeviceDesktopXL24Icon
);
const DeviceDesktopLarge = pickBreakpointIcon(
  DesktopLargeBreakpointIcon,
  DeviceDesktop24Icon
);
const DeviceLaptop = pickBreakpointIcon(
  DesktopStarBreakpointIcon,
  DeviceLaptopStar24Icon
);
const DeviceMobileLandscape = pickBreakpointIcon(
  MobileLandscapeBreakpointIcon,
  DeviceMobileLandscape24Icon
);
const DeviceMobilePortrait = pickBreakpointIcon(
  MobileBreakpointIcon,
  DeviceMobilePortrait24Icon
);
const DeviceTablet = pickBreakpointIcon(
  TabletPortraitBreakpointIcon,
  DeviceTabletPortrait24Icon
);

export type Props = {
  size: 'small' | 'large';
  className?: string;
  style?: React.CSSProperties;
  isActive?: boolean;
  colorVariant?: TextColor;
};

type BaseIconProps = Omit<
  React.ComponentPropsWithoutRef<SpringIconType>,
  'children'
>;
type BaseIcon = React.ForwardRefExoticComponent<
  BaseIconProps & React.RefAttributes<HTMLDivElement>
>;

export const iconsBySize: BreakpointsMap<{
  small: BaseIcon;
  large: BaseIcon;
}> = {
  [BREAKPOINT_ID_XXL]: {
    small: DeviceDesktopXXLarge.Small,
    large: DeviceDesktopXXLarge.Large,
  },
  [BREAKPOINT_ID_XL]: {
    small: DeviceDesktopXLarge.Small,
    large: DeviceDesktopXLarge.Large,
  },
  [BREAKPOINT_ID_LARGE]: {
    small: DeviceDesktopLarge.Small,
    large: DeviceDesktopLarge.Large,
  },
  [BREAKPOINT_ID_MAIN]: {
    small: DeviceLaptop.Small,
    large: DeviceLaptop.Large,
  },
  [BREAKPOINT_ID_MEDIUM]: {
    small: DeviceTablet.Small,
    large: DeviceTablet.Large,
  },
  [BREAKPOINT_ID_SMALL]: {
    small: DeviceMobileLandscape.Small,
    large: DeviceMobileLandscape.Large,
  },
  [BREAKPOINT_ID_TINY]: {
    small: DeviceMobilePortrait.Small,
    large: DeviceMobilePortrait.Large,
  },
};

const createIcon = (breakpointId: BreakpointID) => {
  const BreakpointIcon = ({
    size,
    style,
    className,
    colorVariant,
  }: Omit<Props, 'isActive'>) => {
    const Icon = iconsBySize[breakpointId][size];
    return (
      <Icon
        style={style}
        className={className}
        size={size}
        colorVariant={colorVariant}
      />
    );
  };
  return BreakpointIcon;
};

type IconsMap = BreakpointsMap<React.ComponentType<Props>>;

// @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'IconsMap'.
const Icons: IconsMap = Object.keys(iconsBySize).reduce(
  // @ts-expect-error - TS2769 - No overload matches this call.
  (acc: Partial<IconsMap>, breakpointId: BreakpointID) => {
    acc[breakpointId] = createIcon(breakpointId);
    return acc;
  },
  {} as Partial<IconsMap>
);

export default Icons;
